import { useRef, useState, type ChangeEvent } from 'react';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2EmptyState,
  LoadingState,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiDelete, apiGet, apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import type { JournalAttachmentListResponse } from '../../lib/types/finance.js';

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function sizeLabel(bytes: number): string {
  if (bytes >= 1_000_000)
    return `${(bytes / 1_000_000).toLocaleString('fa-IR', { maximumFractionDigits: 1 })} مگابایت`;
  return `${Math.max(1, Math.round(bytes / 1000)).toLocaleString('fa-IR')} کیلوبایت`;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html — journal detail, supporting documents
 * archetype: TRANSACTION_DETAIL sub-panel
 * status: KIT_CONFORMANT — `.../finance/journals/:id/attachments`. Upload
 *   (PDF/PNG/JPEG/WebP, ≤ 2 MB) at any stage; removal only before the
 *   journal is posted — enforced server-side and by a DB trigger, hidden
 *   here only as a convenience.
 */
export function JournalAttachmentsCard(props: {
  readonly base: string;
  readonly journalId: string;
  readonly posted: boolean;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const path = `${props.base}/api/v1/finance/journals/${props.journalId}/attachments`;
  const list = useApiGet<JournalAttachmentListResponse>(path, [path, refreshKey]);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    const maxBytes = list.status === 'ready' ? list.data.maxBytes : 2_000_000;
    if (file.size > maxBytes) {
      setError(`حجم فایل بیش از ${sizeLabel(maxBytes)} است.`);
      return;
    }
    setBusy(true);
    try {
      await apiPost(path, {
        fileName: file.name,
        contentType: file.type,
        contentBase64: await readAsBase64(file),
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'بارگذاری پیوست ناموفق بود — فقط PDF و تصویر مجاز است.'));
    } finally {
      setBusy(false);
    }
  }

  async function onOpen(id: string, fileName: string) {
    setError(null);
    try {
      const file = await apiGet<{ contentType: string; contentBase64: string }>(`${path}/${id}`);
      const bytes = Uint8Array.from(atob(file.contentBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      setError(bannerMessageFrom(err, 'دریافت پیوست ناموفق بود.'));
    }
  }

  async function onRemove(id: string) {
    setError(null);
    try {
      await apiDelete(`${path}/${id}`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'حذف پیوست ناموفق بود.'));
    }
  }

  return (
    <V2Card>
      <V2CardHeader
        title="پیوست‌ها و مدارک"
        subtitle="فاکتور، رسید بانکی، قرارداد — PDF یا تصویر"
        actions={
          <>
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => void onFile(e)}
            />
            <V2Button
              type="button"
              size="sm"
              variant="secondary"
              icon="plus"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {busy ? 'در حال بارگذاری…' : 'افزودن پیوست'}
            </V2Button>
          </>
        }
      />
      {list.status === 'loading' ? <LoadingState /> : null}
      {list.status === 'ready' && list.data.items.length === 0 ? (
        <V2EmptyState icon="info" title="پیوستی ثبت نشده" compact />
      ) : null}
      {list.status === 'ready' && list.data.items.length > 0 ? (
        <div className="sk-table-scroll">
          <table className="sk-table sk-table--compact">
            <thead>
              <tr>
                <th>نام فایل</th>
                <th style={{ width: 120 }}>حجم</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((a) => (
                <tr key={a.journalAttachmentId}>
                  <td>{a.fileName}</td>
                  <td className="sk-num">{sizeLabel(a.sizeBytes)}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <V2Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => void onOpen(a.journalAttachmentId, a.fileName)}
                    >
                      دریافت
                    </V2Button>
                    {!props.posted ? (
                      <V2Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => void onRemove(a.journalAttachmentId)}
                      >
                        حذف
                      </V2Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {error ? (
        <div style={{ padding: '8px 20px 14px', fontSize: 13, color: 'var(--sk-danger)' }}>
          {error}
        </div>
      ) : null}
    </V2Card>
  );
}
